import React, { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: { product: "Product", system: "The System", resources: "Resources", montreal: "Montreal", quote: "Quote" },
    hero: {
      pin: "Now Available · Montréal, QC",
      stock: "In Stock · 2026",
      overline: "FLAGSHIP / DECORATIVE CONCRETE SYSTEM",
      h1a: "The architecture", h1b: "of exposed", h1c: "aggregate.",
      sub: "Decorative Surface Retarder — a water-based, color-coded system that turns concrete into a precision-engineered finish. Twelve depths of etch. Zero guesswork.",
      cta: "Call Today · 514-594-1482", explore: "Explore the Product",
      stats: [
        { v: "12", l: "Depths of Etch" },
        { v: "100%", l: "Water-Based" },
        { v: "1×", l: "Color-Coded System" },
        { v: "QC", l: "Montréal Distribution" },
      ],
    },
    product: {
      overline: "THE PRODUCT",
      title: "TOP-CAST® Decorative",
      title2: "Surface Retarder.",
      sub: "A top-surface retarder built for the most demanding architectural concrete work. Engineered to deliver consistent, repeatable exposed-aggregate finishes — precisely, every pour.",
      features: [
        { title: "Water-Based", body: "No need for plastic protection during cure. Easier site logistics, safer handling, lower environmental impact." },
        { title: "12 Depths of Etch", body: "From a whisper of texture to a deep aggregate reveal — twelve precise levels, each engineered for a specific architectural intent." },
        { title: "Color & Number Coded", body: "Every depth is color- and number-coded. Zero ambiguity on the jobsite — specify, order, install with confidence." },
        { title: "Trusted Pedigree", body: "By Dayton Superior. A trademark of GCP Applied Technologies. Industry-proven across decades of architectural concrete." },
      ],
      callout: { overline: "FROM FORM TO FINISH", h: "Specify the texture.", hl: "Deliver the art.",
        bullets: [
          "Apply directly to forms or freshly placed concrete",
          "No plastic covering required during cure",
          "Consistent reveal across the entire pour",
          "Compatible with the complete TOP-CAST® Finish System",
        ], video: "Watch the instructional video",
      },
    },
    etch: {
      overline: "DEPTHS OF ETCH",
      h: "Twelve textures.", h2: "One precision system.",
      sub: "Each depth is color- and number-coded so architects, contractors and finishers all speak the same language — from spec sheet to final pour.",
      hint: "Hover the swatches to preview each etch depth — a tactile language for architectural intent.",
      depth: "Depth",
    },
    power: { overline: "POWER IN ACTION", h: "What concrete", h2: "becomes when", h3: "specified precisely.",
      cases: ["Industrial — Etch Depth 06", "Commercial Façade", "Architectural Detail"], cta: "Spec your project — request technical data" },
    system: {
      overline: "THE COMPLETE FINISH SYSTEM",
      h: "Six components.", h2: "One architectural finish.",
      sub: "The TOP-CAST® Decorative Finish System pairs the surface retarder with five companion products — each chosen to protect, extend and refine the exposed-aggregate result.",
      banner: { overline: "SYSTEM IN FOCUS", h: "Engineered to work as one." },
      flagship: "★ FLAGSHIP PRODUCT",
      items: [
        ["Site Saver", "Form release & protection foundation."],
        ["TOP-CAST® Surface Retarder", "The core: defines depth of etch."],
        ["Wash-Off Extender", "Extends working window for wash-off."],
        ["Penetrating Protector", "Densifies & protects exposed aggregate."],
        ["Cure and Seal", "Cures, seals, locks the finish."],
        ["High Gloss Sealer", "Final glossy architectural finish."],
      ],
    },
    resources: {
      overline: "DOCUMENTATION",
      h: "Spec sheets,", h2: "direct from Dayton.",
      sub: "Download the official technical documentation for the TOP-CAST® system — share with architects, specifiers and crews.",
      footer: "Documents hosted by Dayton Superior. TOP-CAST® is a trademark of GCP Applied Technologies.",
      docs: [
        { title: "TOP-CAST® Surface Retarder", sub: "Technical Data Sheet", desc: "Full product specifications, application rates, etch depth chart, cleanup & safety.", badge: "PRIMARY" },
        { title: "TOP-CAST® Architectural Brochure", sub: "Product Brochure", desc: "Visual reference for the complete decorative finish system and real-world finishes.", badge: "BROCHURE" },
        { title: "TOP-CAST® User Guide", sub: "Application Guide", desc: "Step-by-step application instructions for the entire TOP-CAST® finish system.", badge: "GUIDE" },
      ],
      download: "Download PDF",
    },
    montreal: {
      avail: "NOW AVAILABLE", h: "In stock.", h2: "In", mtl: "Montréal.",
      sub: "The full TOP-CAST® system is stocked and ready in the greater Montréal area — for architects, GCs and decorative concrete specialists. Pick up or local delivery available.",
      stats: [["Local Delivery", "Greater Montréal"], ["Lead Time", "Same-Week"], ["Headquarters", "Mascouche, QC"]],
      cta: "CALL TODAY · QUOTES IN MINUTES",
      mapOverline: "OUR LOCATION", mapServ: "Serving Greater Montréal · Laval · Rive-Sud · Rive-Nord",
      directions: "Get Directions ↗",
    },
    quote: {
      overline: "REQUEST A QUOTE", h: "Let's spec", h2: "your project.",
      sub: "Share your project details and our team will reply with technical recommendations, pricing and lead times — typically within one business day.",
      realProjects: "REAL PROJECTS", builtCaption: "Built across Greater Montréal",
      callUs: "CALL US", emailUs: "EMAIL US", visitUs: "VISIT US", serving: "Serving Greater Montréal",
      fields: { name: "Full Name", company: "Company (optional)", email: "Email", phone: "Phone", project: "Project Type", projectPh: "Commercial / Architectural / Residential…", etch: "Target Etch Depth", etchPh: "01–12 or 'unsure'", details: "Project Details", detailsPh: "Tell us about your pour, finish goals, timeline…" },
      reply: "We reply within 1 business day", send: "Send Request", sending: "Sending…",
      successH: "Request received.", successP: (name) => `Thank you, ${name || "friend"}. Our team will reach out shortly with the next steps. For urgent requests, call`,
    },
    footer: {
      tagline: "Your trusted partner for high-end architectural and decorative concrete materials in Greater Montréal.",
      contact: "Contact", resources: "Resources",
      links: ["TOP-CAST® Surface Retarder", "Decorative Finish System", "Spec Sheets (PDF)", "Request a Quote"],
      terms: "Terms of Service", privacy: "Privacy Policy",
      trademark: "TOP-CAST® is a trademark of GCP Applied Technologies.",
      rights: "All rights reserved.",
    },
    sticky: "Call Today · 514-594-1482",
  },
  fr: {
    nav: { product: "Produit", system: "Le Système", resources: "Documents", montreal: "Montréal", quote: "Devis" },
    hero: {
      pin: "Maintenant disponible · Montréal, QC",
      stock: "En stock · 2026",
      overline: "PRODUIT VEDETTE / SYSTÈME DE BÉTON DÉCORATIF",
      h1a: "L'architecture", h1b: "du granulat", h1c: "exposé.",
      sub: "Retardateur de surface décoratif — un système à base d'eau, codé par couleur, qui transforme le béton en finition d'une précision d'ingénierie. Douze profondeurs de gravure. Aucune approximation.",
      cta: "Appelez aujourd'hui · 514-594-1482", explore: "Découvrir le produit",
      stats: [
        { v: "12", l: "Profondeurs de gravure" },
        { v: "100%", l: "À base d'eau" },
        { v: "1×", l: "Système codé par couleur" },
        { v: "QC", l: "Distribution Montréal" },
      ],
    },
    product: {
      overline: "LE PRODUIT",
      title: "TOP-CAST® Retardateur",
      title2: "de surface décoratif.",
      sub: "Un retardateur de surface conçu pour les travaux de béton architectural les plus exigeants. Conçu pour offrir des finitions de granulat exposé constantes et reproductibles — précisément, à chaque coulée.",
      features: [
        { title: "À base d'eau", body: "Aucune protection plastique requise pendant le durcissement. Logistique simplifiée, manipulation plus sûre, impact environnemental réduit." },
        { title: "12 profondeurs de gravure", body: "D'une texture subtile à une révélation profonde du granulat — douze niveaux précis, chacun conçu pour une intention architecturale spécifique." },
        { title: "Codé par couleur et numéro", body: "Chaque profondeur est codée par couleur et numéro. Aucune ambiguïté sur le chantier — spécifier, commander, installer en toute confiance." },
        { title: "Pédigrée de confiance", body: "Par Dayton Superior. Une marque de GCP Applied Technologies. Éprouvée dans l'industrie sur des décennies de béton architectural." },
      ],
      callout: { overline: "DU COFFRAGE À LA FINITION", h: "Spécifiez la texture.", hl: "Livrez l'art.",
        bullets: [
          "Appliqué directement sur les coffrages ou le béton frais",
          "Aucune couverture plastique requise pendant le durcissement",
          "Révélation constante sur l'ensemble de la coulée",
          "Compatible avec le système de finition TOP-CAST® complet",
        ], video: "Voir la vidéo d'instructions",
      },
    },
    etch: {
      overline: "PROFONDEURS DE GRAVURE",
      h: "Douze textures.", h2: "Un système de précision.",
      sub: "Chaque profondeur est codée par couleur et numéro afin que les architectes, entrepreneurs et finisseurs parlent tous le même langage — de la fiche technique à la coulée finale.",
      hint: "Survolez les échantillons pour prévisualiser chaque profondeur — un langage tactile au service de l'intention architecturale.",
      depth: "Profondeur",
    },
    power: { overline: "LA PUISSANCE EN ACTION", h: "Ce que devient le béton", h2: "lorsqu'il est", h3: "spécifié avec précision.",
      cases: ["Industriel — Profondeur 06", "Façade commerciale", "Détail architectural"], cta: "Spécifiez votre projet — demandez les données techniques" },
    system: {
      overline: "LE SYSTÈME DE FINITION COMPLET",
      h: "Six composants.", h2: "Une finition architecturale.",
      sub: "Le système de finition décorative TOP-CAST® associe le retardateur de surface à cinq produits complémentaires — chacun choisi pour protéger, prolonger et raffiner le résultat de granulat exposé.",
      banner: { overline: "SYSTÈME EN VEDETTE", h: "Conçus pour agir ensemble." },
      flagship: "★ PRODUIT VEDETTE",
      items: [
        ["Site Saver", "Démoulant et protection de base."],
        ["TOP-CAST® Retardateur de surface", "Le cœur : définit la profondeur de gravure."],
        ["Prolongateur de rinçage", "Étend la fenêtre de travail pour le rinçage."],
        ["Protecteur pénétrant", "Densifie et protège le granulat exposé."],
        ["Cure & Scellant", "Cure, scelle, fige la finition."],
        ["Scellant haute brillance", "Finition architecturale haute brillance finale."],
      ],
    },
    resources: {
      overline: "DOCUMENTATION",
      h: "Fiches techniques,", h2: "directement de Dayton.",
      sub: "Téléchargez la documentation technique officielle du système TOP-CAST® — à partager avec les architectes, prescripteurs et équipes.",
      footer: "Documents hébergés par Dayton Superior. TOP-CAST® est une marque de GCP Applied Technologies.",
      docs: [
        { title: "TOP-CAST® Retardateur de surface", sub: "Fiche technique", desc: "Spécifications complètes, taux d'application, tableau des profondeurs, nettoyage et sécurité.", badge: "PRIMAIRE" },
        { title: "Brochure architecturale TOP-CAST®", sub: "Brochure produit", desc: "Référence visuelle du système de finition décorative complet et des finitions réelles.", badge: "BROCHURE" },
        { title: "Guide d'utilisation TOP-CAST®", sub: "Guide d'application", desc: "Instructions étape par étape pour l'application du système de finition TOP-CAST® complet.", badge: "GUIDE" },
      ],
      download: "Télécharger le PDF",
    },
    montreal: {
      avail: "MAINTENANT DISPONIBLE", h: "En stock.", h2: "À", mtl: "Montréal.",
      sub: "Le système TOP-CAST® complet est en stock et disponible dans la grande région de Montréal — pour les architectes, entrepreneurs et spécialistes du béton décoratif. Cueillette ou livraison locale.",
      stats: [["Livraison locale", "Grand Montréal"], ["Délai", "Même semaine"], ["Siège social", "Mascouche, QC"]],
      cta: "APPELEZ AUJOURD'HUI · DEVIS EN MINUTES",
      mapOverline: "NOTRE EMPLACEMENT", mapServ: "Desservant le Grand Montréal · Laval · Rive-Sud · Rive-Nord",
      directions: "Obtenir l'itinéraire ↗",
    },
    quote: {
      overline: "DEMANDER UN DEVIS", h: "Spécifions", h2: "votre projet.",
      sub: "Partagez les détails de votre projet et notre équipe vous répondra avec des recommandations techniques, des prix et des délais — généralement en un jour ouvrable.",
      realProjects: "PROJETS RÉELS", builtCaption: "Construits dans le Grand Montréal",
      callUs: "APPELEZ-NOUS", emailUs: "ÉCRIVEZ-NOUS", visitUs: "VISITEZ-NOUS", serving: "Desservant le Grand Montréal",
      fields: { name: "Nom complet", company: "Entreprise (facultatif)", email: "Courriel", phone: "Téléphone", project: "Type de projet", projectPh: "Commercial / Architectural / Résidentiel…", etch: "Profondeur de gravure visée", etchPh: "01–12 ou « incertain »", details: "Détails du projet", detailsPh: "Parlez-nous de votre coulée, objectifs de finition, échéancier…" },
      reply: "Nous répondons en 1 jour ouvrable", send: "Envoyer la demande", sending: "Envoi…",
      successH: "Demande reçue.", successP: (name) => `Merci, ${name || "ami"}. Notre équipe vous contactera sous peu pour la suite. Pour les demandes urgentes, appelez le`,
    },
    footer: {
      tagline: "Votre partenaire de confiance pour les matériaux de béton architectural et décoratif haut de gamme dans le Grand Montréal.",
      contact: "Contact", resources: "Documents",
      links: ["TOP-CAST® Retardateur de surface", "Système de finition décorative", "Fiches techniques (PDF)", "Demander un devis"],
      terms: "Conditions d'utilisation", privacy: "Politique de confidentialité",
      trademark: "TOP-CAST® est une marque de GCP Applied Technologies.",
      rights: "Tous droits réservés.",
    },
    sticky: "Appelez aujourd'hui · 514-594-1482",
  },
};

const LangContext = createContext({ lang: "en", t: dict.en, setLang: () => {} });

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("mily_lang") || (navigator.language?.startsWith("fr") ? "fr" : "en");
  });
  useEffect(() => {
    localStorage.setItem("mily_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  return (
    <LangContext.Provider value={{ lang, t: dict[lang], setLang }}>
      {children}
    </LangContext.Provider>
  );
};

export const useT = () => useContext(LangContext);
